import { paymentAbi } from "@/abis/payment";
import { ArrowLeftIcon } from "@/components/icons";
import { PAYMENT_CONTRACT } from "@/constants/paymentTokens";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { formatUnits, parseUnits } from "viem";
import { erc20Abi } from "viem";
import {
	useAccount,
	useChainId,
	usePublicClient,
	useSwitchChain,
	useWalletClient,
} from "wagmi";
import { Button } from "~/components/ui/button";
import { getUser } from "~/lib/auth/store";
import { type Token } from "../../../constants/paymentTokens";
import {
	type OrderProduct,
	PaymentChannel,
	createOrder,
	getOrderByOrderId,
} from "./api";
import { useFormattedTokens } from "./useFormattedTokens";

interface CryptoPayProps {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	// amount: number;
	onChangeWallet: () => void;
	product: OrderProduct;
}

// TokenItem 组件
const TokenItem = ({
	token,
	isSelected,
	onClick,
	selectedChainIdx,
	onChainClick,
}: {
	token: Token;
	isSelected: boolean;
	onClick: () => void;
	selectedChainIdx: number;
	onChainClick: (chainIdx: number) => void;
}) => (
	<div className="w-full flex flex-col gap-2 bg-bg1 mb-2 rounded-[20px] overflow-hidden">
		<div
			className={cn(
				"flex items-center h-11 hover:bg-c1 text-t1 justify-between rounded-full px-4 py-2 cursor-pointer",
				isSelected && "bg-c1",
			)}
			onClick={onClick}
		>
			<div className="flex items-center gap-2">
				<div className="w-[33px] h-[33px] relative">
					<img
						src={token.icon}
						alt={token.symbol}
						className="w-full h-full object-fill"
					/>
					<div className="w-3 h-3 flex items-center justify-center bg-[#EDF1FF] p-0.5 rounded-full absolute right-0 bottom-0">
						<img
							src={token.chains[selectedChainIdx].chainIcon}
							alt={token.chains[selectedChainIdx].chainName}
							className="w-[8px] h-[8px] object-fill "
						/>
					</div>
				</div>
				<span className="text-sm">{token.symbol}</span>
				{/* <span className="text-xs ml-2">
						{token.chains[selectedChainIdx].chainName}
					</span> */}
			</div>
			<div className="flex items-center gap-1">
				<span>{m.balance()} </span>
				<span className="font-bold">
					{formatUnits(
						token.chains[selectedChainIdx].balance as bigint,
						token.chains[selectedChainIdx].decimals ?? 0,
					)}
				</span>
				<ArrowLeftIcon
					className={cn(
						"[&>path]:stroke-t2 w-3 h-3 rotate-180",
						isSelected && "rotate-270",
					)}
				/>
			</div>
		</div>
		{isSelected && (
			<div className="block">
				{token.chains.map((t, chainIdx) => (
					<div
						key={`chain-${t.chainId}`}
						className={cn(
							"flex items-center justify-between hover:opacity-80 cursor-pointer px-4 py-2",
							// selectedChainIdx === chainIdx && "bg-c1",
						)}
						onClick={() => onChainClick(chainIdx)}
					>
						<div className="flex items-center gap-2">
							<div className="w-[33px] h-[33px] relative">
								<img
									src={token.icon}
									alt={token.symbol}
									className="w-full h-full object-fill"
								/>
								<div className="w-3 h-3 flex items-center justify-center bg-[#EDF1FF] p-0.5 rounded-full absolute right-0 bottom-0">
									<img
										src={t.chainIcon}
										alt={t.chainName}
										className="w-[8px] h-[8px] object-fill "
									/>
								</div>
							</div>
							<span className="text-sm">{token.symbol}</span>
							{t.net && <span className="text-xs ml-2">({t.net})</span>}
							{/* <span className="text-xs ml-2">{t.chainName}</span> */}
						</div>
						<div className="flex items-center gap-1 pr-4">
							<span className="font-bold">
								{formatUnits(t.balance as bigint, t.decimals ?? 0)}
							</span>
						</div>
					</div>
				))}
			</div>
		)}
	</div>
);

const WalletInfo = ({
	address,
	onChangeWallet,
}: {
	address?: string;
	onChangeWallet: () => void;
}) => (
	<div className="w-full flex items-center justify-between bg-bg1 rounded-full px-2 py-[10px] h-10 text-t2 text-sm">
		<span>{m.your_wallet()}</span>
		<div className="flex items-center gap-2">
			<span className="text-t1">
				{address?.slice(0, 5)}...{address?.slice(-4)}
			</span>
			<span className="text-main1 cursor-pointer" onClick={onChangeWallet}>
				{m.change_wallet()}
			</span>
		</div>
	</div>
);

export default function CryptoPay({
	open,
	onOpenChange,
	// amount,
	onChangeWallet,
	product,
}: CryptoPayProps) {
	const { address } = useAccount();
	const chainId = useChainId();
	const { switchChainAsync } = useSwitchChain();
	const publicClient = usePublicClient();
	const { data: walletClient } = useWalletClient();

	const [selectedTokenIdx, setSelectedTokenIdx] = useState<number | null>(null);
	const [selectedChainIdx, setSelectedChainIdx] = useState(0);
	const [activeTokenIdx, setActiveTokenIdx] = useState(0);
	const [activeChainIdx, setActiveChainIdx] = useState(0);
	const [loading, setLoading] = useState(false);
	const didAutoSelect = useRef(false);

	const formattedTokens = useFormattedTokens(address);

	// 自动选中第一个有效的 token
	useEffect(() => {
		if (!didAutoSelect.current && formattedTokens.length > 0) {
			const validTokenIdx = formattedTokens.findIndex((t) =>
				t.chains.some(
					(c) =>
						c.address &&
						c.address !== "0x0000000000000000000000000000000000000000",
				),
			);
			if (validTokenIdx !== -1) {
				setActiveTokenIdx(validTokenIdx);
				setActiveChainIdx(0);
				setSelectedTokenIdx(validTokenIdx); // 新增：同步展开
				setSelectedChainIdx(0); // 新增：同步展开
				didAutoSelect.current = true; // 只自动一次
			}
		}
	}, [formattedTokens]);

	// 先判断，避免后续访问 undefined
	if (!open || !formattedTokens.length) return null;

	const currentToken = formattedTokens[activeTokenIdx];
	const currentChain = currentToken.chains[activeChainIdx];
	const tokenAddress = currentChain.address;

	// 判断余额是否足够
	const currentBalance = currentChain.balance as bigint;
	const currentDecimals = currentChain.decimals ?? 0;
	const amountInToken = parseUnits(
		product.price.toString() || "0",
		currentDecimals,
	);

	// console.log("currentChain", currentChain);
	const enough = currentBalance >= amountInToken;

	// 合约支付逻辑
	const handlePay = useCallback(async () => {
		setLoading(true);
		try {
			// 校验 token address
			if (
				!tokenAddress ||
				tokenAddress === "0x0000000000000000000000000000000000000000"
			) {
				toast.error(m.pay_not_found_token());
				setLoading(false);
				return;
			}
			const createOrderRes = await createOrder({
				payment_channel: PaymentChannel.Web3,
				product_id: product.id,
			});

			if (createOrderRes.code !== 0) {
				return;
			}

			console.log("createOrderRes", createOrderRes);

			// 1. 检查链
			if (chainId !== currentChain.chainId) {
				await switchChainAsync({ chainId: currentChain.chainId });
			}
			// 2. 检查授权
			const allowance = await publicClient?.readContract({
				address: tokenAddress as `0x${string}`,
				abi: erc20Abi,
				functionName: "allowance",
				args: [address as `0x${string}`, PAYMENT_CONTRACT],
			});
			if (BigInt(allowance ?? 0) < amountInToken) {
				// 授权
				const approveHash = await walletClient?.writeContract({
					address: tokenAddress as `0x${string}`,
					abi: erc20Abi,
					functionName: "approve",
					args: [PAYMENT_CONTRACT, amountInToken],
				});
				await publicClient?.waitForTransactionReceipt({
					hash: approveHash as `0x${string}`,
				});
			}
			// 3. 调用合约支付

			const payHash = await walletClient?.writeContract({
				address: PAYMENT_CONTRACT as `0x${string}`,
				abi: paymentAbi,
				functionName: "pay",
				args: [
					tokenAddress as `0x${string}`,
					amountInToken,
					createOrderRes.data.order_id,
				],
			});
			await publicClient?.waitForTransactionReceipt({
				hash: payHash as `0x${string}`,
			});

			const interval = setInterval(async () => {
				const order = await getOrderByOrderId(createOrderRes.data.order_id);
				if (order.code === 0 && order.data?.order) {
					toast.success(m.pay_success());
					onOpenChange(false);
					setLoading(false);
					clearInterval(interval);
					await getUser();
				}
			}, 2000);
			setLoading(false);
		} catch (e: any) {
			toast.error(e?.message || m.pay_failed());
		} finally {
			setLoading(false);
		}
	}, [
		address,
		chainId,
		currentChain,
		currentToken,
		activeChainIdx,
		amountInToken,
		publicClient,
		walletClient,
		switchChainAsync,
		onOpenChange,
		product.id,
		tokenAddress,
	]);

	// console.log("formattedTokens", formattedTokens);

	return (
		<div className="flex flex-col items-center py-10 w-[400px]">
			<WalletInfo address={address} onChangeWallet={onChangeWallet} />

			<div className="text-5xl font-bold flex justify-center gap-1 items-end my-14 text-t1">
				<span className="text-sm font-medium relative -top-1 mr-1">
					{product?.currency_symbol}
				</span>
				{product?.price}
			</div>

			{/* 币种列表 */}
			{formattedTokens.map((token, idx) => (
				<TokenItem
					key={token.symbol}
					token={token}
					isSelected={selectedTokenIdx === idx}
					onClick={() => {
						if (selectedTokenIdx === idx) {
							setSelectedTokenIdx(null); // 再次点击收起
						} else {
							setSelectedTokenIdx(idx); // 展开
							setSelectedChainIdx(0);
						}
					}}
					selectedChainIdx={selectedTokenIdx === idx ? selectedChainIdx : 0}
					onChainClick={(chainIdx) => {
						setSelectedTokenIdx(idx);
						setSelectedChainIdx(chainIdx);
						setActiveTokenIdx(idx);
						setActiveChainIdx(chainIdx);
					}}
				/>
			))}

			<Button
				className="w-full mt-10 rounded-full h-10 text-sm"
				disabled={
					!enough ||
					loading ||
					!tokenAddress ||
					tokenAddress === "0x0000000000000000000000000000000000000000"
				}
				onClick={handlePay}
			>
				{loading
					? m.pay_loading()
					: !enough
						? m.pay_not_enough()
						: m.pay_button()}
			</Button>
		</div>
	);
}
